import {
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from "react";

import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "tertiary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

const sizes: Record<ButtonSize, { height: number; padding: string; font: string; gap: number; icon: number }> = {
  sm: { height: 32, padding: "0 12px", font: "var(--fs-body-sm)", gap: 6, icon: 16 },
  md: { height: 38, padding: "0 16px", font: "var(--fs-button)", gap: 8, icon: 18 },
  lg: { height: 44, padding: "0 20px", font: "var(--fs-body-lg)", gap: 8, icon: 20 },
};

const variants: Record<ButtonVariant, { background: string; color: string; border: string; hover: string; active: string }> = {
  primary: {
    background: "var(--color-brand-primary)",
    color: "var(--color-text-on-brand)",
    border: "1px solid var(--color-brand-primary)",
    hover: "var(--color-brand-primary-hover)",
    active: "var(--color-brand-primary-active)",
  },
  secondary: {
    background: "var(--neutral-0)",
    color: "var(--color-brand-primary)",
    border: "1px solid var(--color-border-strong)",
    hover: "var(--neutral-50)",
    active: "var(--neutral-100)",
  },
  tertiary: {
    background: "var(--color-surface-selected)",
    color: "var(--color-brand-primary)",
    border: "1px solid transparent",
    hover: "var(--blue-100)",
    active: "var(--blue-200)",
  },
  ghost: {
    background: "transparent",
    color: "var(--color-text-secondary)",
    border: "1px solid transparent",
    hover: "var(--neutral-100)",
    active: "var(--neutral-150)",
  },
  danger: {
    background: "var(--color-status-error)",
    color: "#fff",
    border: "1px solid var(--color-status-error)",
    hover: "var(--red-600)",
    active: "var(--red-700)",
  },
};

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  type?: "button" | "submit" | "reset";
  style?: CSSProperties;
}

/** Primary action control. Five variants × three sizes. */
export function Button({
  children,
  variant = "primary",
  size = "md",
  disabled = false,
  loading = false,
  fullWidth = false,
  iconLeft,
  iconRight,
  onClick,
  type = "button",
  style,
  className,
  ...rest
}: ButtonProps) {
  const [state, setState] = useState<"default" | "hover" | "active">("default");
  const s = sizes[size];
  const v = variants[variant];
  const bg = disabled ? undefined : state === "active" ? v.active : state === "hover" ? v.hover : v.background;
  return (
    <button
      className={["cf-button", className].filter(Boolean).join(" ")}
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      onMouseEnter={() => setState("hover")}
      onMouseLeave={() => setState("default")}
      onMouseDown={() => setState("active")}
      onMouseUp={() => setState("hover")}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: s.gap,
        height: s.height,
        padding: s.padding,
        width: fullWidth ? "100%" : undefined,
        fontSize: s.font,
        fontWeight: "var(--fw-semibold)" as unknown as number,
        fontFamily: "var(--font-sans)",
        lineHeight: 1,
        borderRadius: "var(--radius-md)",
        background: bg ?? v.background,
        color: v.color,
        border: v.border,
        cursor: disabled || loading ? "not-allowed" : "pointer",
        opacity: disabled ? "var(--opacity-disabled)" : 1,
        transition: "background var(--duration-fast) var(--ease-standard)",
        whiteSpace: "nowrap",
        outline: "none",
        ...style,
      }}
      {...rest}
    >
      {loading && <Spinner size={s.icon} color={v.color} thickness={2} />}
      {!loading && iconLeft}
      {children}
      {!loading && iconRight}
    </button>
  );
}
